import React, { useState, useEffect } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import usePersonnelTable from "./usePersonnelTable";
import EditContactDialog from "./EditContactDialog";
import DeleteDialog from "./DeleteDialog";
import Notification from "../notification/Notification";
import { useDispatch, useSelector } from "react-redux";
import { fetchPersonnel, resetPersonnel } from "./personnelSlice";
import { Dropdown } from "primereact/dropdown";
import API_ROUTES from "../../../api-routes";
import NoKeyPersonnel from "./NoKeyPersonnel";
import apiService from "../../../services/service";
import ReactCountryFlag from "react-country-flag";
import { getName } from "country-list";
import { useParams } from "react-router-dom";
import PermissionGate from "~/components/PermissionGate";
import { Skeleton } from "primereact/skeleton";

const PersonnelTable = ({
  successMessage,
  setSuccessMessage,
  showKeyPersonnel,
  personnelState,
  setPersonnelState,
}) => {
  const dispatch = useDispatch();
  const { id } = useParams();
  const personnel = useSelector((state) => state.personnel.personnel);
  const { role, isAdmin } = useSelector((state) => state.login);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    dispatch(fetchPersonnel(id))
      .then(() => {
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }, [dispatch]);

  useEffect(() => {
    return () => {
      dispatch(resetPersonnel(id));
    };
  }, []);

  const {
    visible,
    setVisible,
    editVisible,
    setEditVisible,
    isAddMode,
    rowToEdit,
    setRowToEdit,
    message,
    setMessage,
    first,
    rows,
    onPageChange,
    showEditDialog,
    showDeleteDialog,
    deleteRow,
    searchDataArr,
    searchTerm,
    setSearchTerm,
    loading,
    setLoading,
    editMessage,
    setEditMessage,
    location,
    selectedLocation,
    setSelectedLocation,
    rowToDelete,
    searchStateArr,
  } = usePersonnelTable(
    personnel || [],
    personnelState,
    setPersonnelState,
    showKeyPersonnel,
  );

  const actionBodyTemplate = (rowData) => {
    return (
      <PermissionGate
        resource="entity"
        action="modify"
        resourceAttr={{ entity_id: id }}
      >
        <div className="flex align-items-center">
          <span
            className="pi pi-pencil ml-4 mr-3 pencil-pointer"
            onClick={() => showEditDialog(rowData)}
          ></span>
          <span
            className="pi pi-trash mr-6 pencil-pointer"
            onClick={() => showDeleteDialog(rowData)}
          ></span>
        </div>
      </PermissionGate>
    );
  };

  const handleSave = async (formData) => {
    setIsLoading(true);
    const personnelData = {
      ...rowToEdit,
      ...formData,
    };

    if (personnelData.phone === 0) {
      personnelData.phone = "";
    }

    if (personnelData.phone_code === 0) {
      personnelData.phone_code = "";
    }

    try {
      if (!showKeyPersonnel) {
        const data = await apiService(API_ROUTES.UPDATE_PERSONNEL, "PUT", {
          replace: { id: personnelData.id },
          body: JSON.stringify(personnelData),
        });

        if (data.status) {
          dispatch(fetchPersonnel(id));
          setEditMessage("Key Personnel has been updated.");
        } else {
          setEditMessage(data.message);
        }
      } else {
        setPersonnelState((prevState) =>
          prevState.map((person) => {
            if (person.id === personnelData.id) {
              return {
                ...person,
                ...personnelData,
                country:
                  personnelData.country !== undefined
                    ? personnelData.country
                    : person.country,
              };
            }
            return person;
          }),
        );

        setEditMessage("Key Personnel has been updated.");
      }
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.error("Error:", error);
      setEditMessage("An error occurred while saving contact details");
    }

    setEditVisible(false);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  return (
    <>
      {loading ? (
        <DataTable
          value={[...Array(10)]}
          tableStyle={{ minWidth: "50rem" }}
          className="datatable-right-align"
        >
          <Column
            header="Full Name"
            body={() => <Skeleton width="100%" />}
            sortable
            className="table-col-width"
          ></Column>
          <Column
            header="Role"
            body={() => <Skeleton />}
            sortable
            className="table-col-width"
          ></Column>
          {!["LSP_USER", "LSP_MANAGER"].includes(role) && (
            <Column
              header="Email"
              body={() => <Skeleton />}
              sortable
              className="table-col-width"
            ></Column>
          )}
          {!["LSP_USER", "LSP_MANAGER"].includes(role) && (
            <Column
              header="Phone Number"
              body={() => <Skeleton />}
              sortable
              className="table-col-width"
            ></Column>
          )}
          <Column
            header="Location"
            body={() => <Skeleton />}
            sortable
            className="table-col-width"
          ></Column>
          {isAdmin && (
            <Column
              body={() => <Skeleton />}
              className="table-col-width"
            ></Column>
          )}
        </DataTable>
      ) : searchDataArr &&
        searchDataArr.length === 0 &&
        !searchTerm &&
        !showKeyPersonnel ? (
        <NoKeyPersonnel />
      ) : (
        <div className="formgrid grid">
          <div className="field col-6 mb-0 pl-0">
            <div className="input-wrapper ml-2">
              <span className="pi pi-search span-left"></span>
              <input
                type="text"
                placeholder="Search contact by name, last name, role, email.."
                className="input-field appearance-none outline-none w-full"
                value={searchTerm}
                onChange={handleSearchChange}
              />
              <span className="pi pi-search span-right"></span>
            </div>
          </div>
          <div className="field col-6 mb-0">
            <div className="flex align-items-center justify-content-end gap-2">
              <p className="filter-by">Filter By </p>
              {loading ? (
                <Skeleton className="p-4"></Skeleton>
              ) : (
                <Dropdown
                  value={selectedLocation}
                  onChange={(e) => {
                    setSelectedLocation(e.value);
                    setSearchTerm("");
                  }}
                  options={location}
                  optionLabel="label"
                  placeholder="All Locations"
                  className="w-full md:w-11rem"
                  itemTemplate={(option) => (
                    <div className="flex align-items-center">
                      <ReactCountryFlag
                        countryCode={option.value}
                        svg
                        className="flag-svg-icon"
                      />
                      <span className="ml-2">{option.label}</span>
                    </div>
                  )}
                />
              )}
            </div>
          </div>
          <Notification
            message={message}
            setMessage={setMessage}
            successMessage={successMessage}
            setSuccessMessage={setSuccessMessage}
            editMessage={editMessage}
            setEditMessage={setEditMessage}
          />
          <div className="field col-12 table-section">
            <div className="card">
              <DataTable
                value={searchStateArr ? searchStateArr : searchDataArr}
                sortMode="multiple"
                tableStyle={{ minWidth: "50rem" }}
                paginator={searchDataArr?.length > 10}
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                first={first}
                rows={rows}
                onPage={onPageChange}
                rowsPerPageOptions={[10, 20, 30]}
                className="datatable-right-align"
                emptyMessage="No results were found"
              >
                <Column
                  field="name"
                  body={(rowData) =>
                    rowData?.preferred_name ||
                    [rowData.name, rowData.other_name, rowData.last_name]
                      .filter(Boolean)
                      .join(" ")
                  }
                  header="Full Name"
                  sortable
                  className="table-col-width"
                ></Column>

                <Column
                  field="role"
                  header="Role"
                  sortable
                  className="table-col-width"
                ></Column>
                {!["LSP_USER", "LSP_MANAGER"].includes(role) && (
                  <Column
                    field="email"
                    header="Email"
                    sortable
                    className="table-col-width"
                  ></Column>
                )}
                {!["LSP_USER", "LSP_MANAGER"].includes(role) && (
                  <Column
                    field="phone_code"
                    body={(rowData) =>
                      rowData?.phone_code && rowData?.phone
                        ? `+${rowData.phone_code} ${rowData.phone}`
                        : rowData?.phone
                          ? rowData?.phone
                          : "-"
                    }
                    header="Phone Number"
                    sortable
                    className="table-col-width"
                  ></Column>
                )}
                <Column
                  field="country"
                  header="Location"
                  body={(rowData) => (
                    <>
                      <ReactCountryFlag
                        countryCode={rowData?.country || "N/A"}
                        svg
                        className="flag-svg-icon"
                      />
                      {getName(rowData?.country || "N/A")}
                    </>
                  )}
                  sortable
                  className="table-col-width"
                ></Column>
                {isAdmin && (
                  <Column
                    body={actionBodyTemplate}
                    className="table-col-width"
                  ></Column>
                )}
              </DataTable>
            </div>
          </div>
        </div>
      )}
      <DeleteDialog
        visible={visible}
        onHide={() => setVisible(false)}
        onConfirm={deleteRow}
        rowToDelete={rowToDelete}
      />

      <EditContactDialog
        visible={editVisible}
        onHide={() => setEditVisible(false)}
        rowToEdit={rowToEdit}
        setRowToEdit={setRowToEdit}
        onSave={handleSave}
        isAddMode={isAddMode}
        isLoading={isLoading}
      />
    </>
  );
};

export default PersonnelTable;
